import { Component, computed, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, distinctUntilChanged, filter, finalize, map, merge, Observable, switchMap, take } from 'rxjs';
import { ButtonComponent } from '@shared/components/button';
import { ModalService } from '@shared/components/modal';
import { FieldErrorDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { PillComponent } from '@shared/components/pill';
import { SegmentOption, SegmentedControlComponent } from '@shared/components/segmented-control';
import { SpinnerComponent } from '@shared/components/spinner';
import { ToggleComponent } from '@shared/components/toggle';
import { TooltipDirective } from '@shared/directives/tooltip';
import {
  AccesApplicationConnect,
  ApplicationConnect,
  FrontConnect,
  LONGUEURS_MAXIMALES,
  NatureServiceConnect,
  PartenaireConnect,
  TenantApplicationConnect,
  TenantFrontConnect,
  TenantPartenaireConnect,
  VisibiliteConnect,
} from '../../liste-clients.interface';
import { ListeClientsService } from '../../liste-clients.service';
import {
  DialogPartenaireConnectComponent,
  DialogPartenaireConnectData,
  DialogPartenaireConnectOutput,
} from '../dialog-partenaire-connect/dialog-partenaire-connect.component';

// Partenaire ITR (Connect:PartnerConfigurations) : l'accès au client sélectionné porte la liste des
// codes des interfaces Aparté du partenaire sur ce client (une par lieu ou par marque)
export interface TuilePartenaire {
  partenaire: PartenaireConnect;
  cochee: boolean;
  // L'accès du partenaire au client sélectionné (TenantId == identifiant)
  accesExistant?: TenantPartenaireConnect;
  // Accès existant que le framework refuserait : aucun code, un code invalide ou en double
  codesADefinir: boolean;
  codes: FormArray<FormControl<string>>;
}

// Application client (Connect:ClientConfigurations) : l'accès au client sélectionné porte une
// visibilité, saisie dans le formulaire que la bascule déplie
export interface TuileApplication {
  application: ApplicationConnect;
  cochee: boolean;
  // L'accès de l'application au client sélectionné (TenantId == identifiant)
  accesExistant?: TenantApplicationConnect;
  // Accès existant que le framework refuserait : Visibility absente ou invalide, LoginId incohérent
  visibiliteADefinir: boolean;
  formulaire: FormGroup<{
    visibilite: FormControl<VisibiliteConnect>;
    loginId: FormControl<string>;
  }>;
}

// Front web (Connect:FrontConfigurations) : l'accès au client sélectionné est son seul réglage
export interface TuileFront {
  front: FrontConnect;
  cochee: boolean;
  accesExistant?: TenantFrontConnect;
}

// Sous-section d'une famille d'étiquettes, c'est-à-dire d'une API (TempsReel, Crm, VenteWeb...) :
// pour le client sélectionné, une bascule par partenaire ITR (Connect:PartnerConfigurations, la
// tuile déplie la liste des codes interface de l'accès, enregistrée par un bouton), une bascule par
// application client (Connect:ClientConfigurations, la tuile déplie la visibilité de l'accès,
// enregistrée par un bouton) et une bascule par front (Connect:FrontConfigurations : l'accès est son seul réglage, la
// bascule l'écrit directement). Le type d'une famille se lit dans son contenu ; une famille qui en
// porte plusieurs affiche toutes les sortes de lignes
@Component({
  selector: 'app-connect-famille',
  imports: [
    ButtonComponent,
    FieldErrorDirective,
    FormFieldComponent,
    InputComponent,
    PillComponent,
    SegmentedControlComponent,
    SpinnerComponent,
    ToggleComponent,
    TooltipDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './connect-famille.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './connect-famille.component.scss',
})
export class ConnectFamilleComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  protected readonly optionsVisibilite: SegmentOption<VisibiliteConnect>[] = [
    { value: 'Restricted', kind: 'text', label: 'Restreinte' },
    { value: 'Unrestricted', kind: 'text', label: 'Non restreinte' },
  ];

  protected readonly chiffresSeulement = /[0-9]/;

  private listeClientsService = inject(ListeClientsService);
  private modal = inject(ModalService);

  famille = input.required<string>();

  // Types connus du service : ceux qu'il porte dans n'importe quel environnement du store, ou
  // celui choisi à sa création s'il n'est pas encore persisté. Ils décident des boutons d'ajout,
  // même quand la famille est vide dans l'environnement courant
  natures = input<NatureServiceConnect[]>([]);

  chargementEnCours = signal<boolean>(false);
  enregistrementEnCours = signal<boolean>(false);

  // Les tuiles sont modifiées sur place (bascule, saisie) : `revision` signale ces écritures au
  // gabarit, qui lit les tuiles par les accesseurs ci-dessous
  private readonly revision = signal(0);
  private readonly tuilesPartenaires = signal<TuilePartenaire[]>([]);
  private readonly tuilesApplicationsChargees = signal<TuileApplication[]>([]);
  private readonly tuilesFrontsChargees = signal<TuileFront[]>([]);

  get tuiles(): TuilePartenaire[] {
    this.revision();
    return this.tuilesPartenaires();
  }

  get tuilesApplications(): TuileApplication[] {
    this.revision();
    return this.tuilesApplicationsChargees();
  }

  get tuilesFronts(): TuileFront[] {
    this.revision();
    return this.tuilesFrontsChargees();
  }

  private rechargement = signal(0);

  private demande = computed(() => ({
    famille: this.famille(),
    environnement: this.listeClientsService.environnementSelectionne(),
    client: this.listeClientsService.tenantSelectionne()?.identifier,
    version: this.rechargement(),
  }));

  private contenuCharge = toSignal(
    toObservable(this.demande).pipe(
      filter(demande => !!demande.environnement && !!demande.client),
      // Le computed produit un nouvel objet à chaque réévaluation, même à valeurs égales
      // (la sélection peut être réécrite, par exemple après un Valider de la carte
      // informations client) : sans ce garde-fou, les tuiles seraient reconstruites et
      // une saisie de codes interface en cours serait perdue
      distinctUntilChanged(
        (precedente, courante) =>
          precedente.famille === courante.famille &&
          precedente.environnement === courante.environnement &&
          precedente.client === courante.client &&
          precedente.version === courante.version,
      ),
      switchMap(demande => {
        this.chargementEnCours.set(true);
        return combineLatest([
          this.listeClientsService.recupererPartenairesConnect$(demande.famille),
          this.listeClientsService.recupererApplicationsConnect$(demande.famille),
          this.listeClientsService.recupererFrontsConnect$(demande.famille),
        ]).pipe(
          // Le client de la demande voyage avec la réponse : l'effect de construction des
          // tuiles ne doit lire aucun signal supplémentaire — relire tenantSelectionne()
          // l'abonnerait à la sélection et reconstruirait les tuiles (FormGroups neufs,
          // saisie perdue) à chaque réécriture, même filtrée par le distinctUntilChanged
          map(([partenaires, applications, fronts]) => ({
            partenaires,
            applications,
            fronts,
            client: demande.client!,
          })),
          finalize(() => this.chargementEnCours.set(false)),
        );
      }),
    ),
  );

  // Ce que la famille porte, pour choisir le bouton d'ajout : ses lignes dans l'environnement
  // courant priment, car un autre environnement peut porter un reste d'un autre type. Sans ligne
  // ici, ses types connus (natures) ; sans aucun des deux, le comportement historique : des
  // partenaires ITR
  porteDesPartenaires = computed(() => this.porte('partenaire'));

  porteDesApplications = computed(() => this.porte('application'));

  porteDesFronts = computed(() => this.porte('front'));

  private porte(nature: NatureServiceConnect): boolean {
    const contenu = this.contenuCharge();
    if (contenu && this.porteDesLignes()) {
      const lignes = { partenaire: contenu.partenaires, application: contenu.applications, front: contenu.fronts };
      return lignes[nature].length > 0;
    }
    return this.natures().includes(nature) || (nature === 'partenaire' && this.natures().length === 0);
  }

  private porteDesLignes = computed(() => {
    const contenu = this.contenuCharge();
    return (
      !!contenu && (contenu.partenaires.length > 0 || contenu.applications.length > 0 || contenu.fronts.length > 0)
    );
  });

  constructor() {
    effect(onCleanup => {
      const charge = this.contenuCharge();
      if (charge) {
        const tuilesPartenaires = construireTuilesPartenaires(charge.partenaires, charge.client);
        const tuilesApplications = construireTuilesApplications(charge.applications, charge.client);
        // Le gabarit lit les codes, la visibilité et le login saisis ; l'abonnement est coupé au
        // rechargement des tuiles et à la destruction du composant
        const saisies = merge(
          ...tuilesPartenaires.map(tuile => tuile.codes.valueChanges),
          ...tuilesApplications.map(tuile => tuile.formulaire.valueChanges),
        ).subscribe(() => this.signalerModification());
        onCleanup(() => saisies.unsubscribe());
        this.tuilesPartenaires.set(tuilesPartenaires);
        this.tuilesApplicationsChargees.set(tuilesApplications);
        this.tuilesFrontsChargees.set(construireTuilesFronts(charge.fronts, charge.client));
      }
    });
  }

  private signalerModification() {
    this.revision.update(version => version + 1);
  }

  basculer(tuile: TuilePartenaire, cochee: boolean) {
    tuile.cochee = cochee;
    this.signalerModification();
  }

  // Désélection validée : les clés de l'accès de ce client (client et codes) sont retirées du store.
  // Le client attendu est celui affiché : sur une tuile périmée, le serveur refuse le retrait
  supprimerAccesPartenaire(tuile: TuilePartenaire) {
    const client = this.listeClientsService.tenantSelectionne()?.identifier;
    const indexTenant = tuile.accesExistant?.index;
    if (!client || indexTenant === undefined || this.enregistrementEnCours() || this.chargementEnCours()) {
      return;
    }

    this.executer(
      this.listeClientsService.supprimerAccesPartenaireConnect$(
        this.famille(),
        tuile.partenaire.index,
        indexTenant,
        client,
      ),
    );
  }

  // Plafond partagé avec le backend : au-delà, l'action ne tiendrait plus dans le journal d'audit
  peutAjouterCode(tuile: TuilePartenaire): boolean {
    return tuile.codes.length < this.longueurs.codesInterfaceParAcces;
  }

  ajouterCode(tuile: TuilePartenaire) {
    if (!this.peutAjouterCode(tuile)) {
      return;
    }

    tuile.codes.push(nouveauCode(''));
    this.signalerModification();
  }

  // Un accès porte au moins un code : le dernier champ ne se retire pas
  retirerCode(tuile: TuilePartenaire, rang: number) {
    if (tuile.codes.length > 1) {
      tuile.codes.removeAt(rang);
      this.signalerModification();
    }
  }

  // Message du champ, ou undefined s'il est vide ou valide
  erreurCode(tuile: TuilePartenaire, rang: number): string | undefined {
    const valeur = tuile.codes.at(rang).value.trim();
    if (!valeur) {
      return undefined;
    }

    if (!entierPositifValide(valeur)) {
      return 'Entier positif, au plus 2147483647';
    }

    const precedents = tuile.codes
      .getRawValue()
      .slice(0, rang)
      .map(code => code.trim())
      .filter(entierPositifValide)
      .map(Number);
    return precedents.includes(Number(valeur)) ? 'Code déjà saisi' : undefined;
  }

  // Au moins un code, tous entiers positifs et sans doublon : règles du framework
  peutEnregistrerAccesPartenaire(tuile: TuilePartenaire): boolean {
    return !!codesSaisis(tuile);
  }

  // Le même formulaire active le client chez le partenaire ou remplace les codes de l'accès
  // existant. Le client et ses codes seulement, comme pour un front : le libellé et le client id
  // affichés ne sont pas renvoyés, pour ne pas écraser une modification faite ailleurs
  enregistrerAccesPartenaire(tuile: TuilePartenaire) {
    const codes = codesSaisis(tuile);
    const client = this.listeClientsService.tenantSelectionne()?.identifier;
    if (!codes || !client || this.enregistrementEnCours() || this.chargementEnCours()) {
      return;
    }

    this.enregistrementEnCours.set(true);
    this.listeClientsService
      .enregistrerPartenaireConnect$(this.famille(), {
        index: tuile.partenaire.index,
        tenants: [{ index: tuile.accesExistant?.index ?? null, tenantId: client, codeInterfaces: codes }],
      })
      .pipe(
        take(1),
        finalize(() => this.enregistrementEnCours.set(false)),
      )
      // Rechargé sur succès seulement : sur un refus, la saisie reste à corriger
      .subscribe(() => this.recharger());
  }

  // Application client, comme un partenaire : activée, la bascule déplie le formulaire de
  // visibilité, l'accès n'est écrit qu'à l'enregistrement. Désactivée sur un accès existant, elle
  // demande de valider le retrait, qui efface la visibilité et le login saisis
  basculerAcces(tuile: TuileApplication, cochee: boolean) {
    if (this.enregistrementEnCours() || this.chargementEnCours()) {
      return;
    }

    tuile.cochee = cochee;
    this.signalerModification();
  }

  // Désélection validée : les clés de l'accès de ce client sont retirées du store
  supprimerAcces(tuile: TuileApplication) {
    if (this.enregistrementEnCours() || this.chargementEnCours()) {
      return;
    }

    this.executer(this.retirerAccesApplication$(tuile));
  }

  // Login saisi mais refusé : le bouton Enregistrer est grisé, le champ dit pourquoi
  loginIdInvalide(tuile: TuileApplication): boolean {
    const { visibilite, loginId } = tuile.formulaire.getRawValue();
    return visibilite === 'Restricted' && !!loginId && !entierPositifValide(loginId);
  }

  // Le restreint exige un login positif, le non restreint n'en porte pas : règles du framework
  peutEnregistrerAcces(tuile: TuileApplication): boolean {
    return !!accesSaisi(tuile);
  }

  // Le même formulaire accorde un nouvel accès ou modifie la visibilité de l'accès existant
  enregistrerAcces(tuile: TuileApplication) {
    const acces = accesSaisi(tuile);
    const client = this.listeClientsService.tenantSelectionne()?.identifier;
    if (!acces || !client || this.enregistrementEnCours() || this.chargementEnCours()) {
      return;
    }

    const indexTenant = tuile.accesExistant?.index;
    this.executer(
      indexTenant === undefined || indexTenant === null
        ? this.listeClientsService.accorderAccesApplicationConnect$(
            this.famille(),
            tuile.application.index,
            client,
            acces,
          )
        : this.listeClientsService.modifierAccesApplicationConnect$(
            this.famille(),
            tuile.application.index,
            indexTenant,
            client,
            acces,
          ),
    );
  }

  // Front web : la bascule EST l'action, sans confirmation. Activée, elle donne au front l'accès
  // au client sélectionné ; désactivée, elle le retire du store
  basculerAccesFront(tuile: TuileFront, cochee: boolean) {
    if (this.enregistrementEnCours() || this.chargementEnCours()) {
      return;
    }

    this.executer(cochee ? this.accorderAccesFront$(tuile) : this.retirerAccesFront$(tuile));
  }

  // Le store fait foi : après l'appel, réussi ou non, les tuiles sont relues et chaque bascule
  // affiche l'état réel. Sur un échec, l'intercepteur HTTP affiche l'erreur et termine le flux sans
  // la propager : seul ce rechargement remet la bascule dans le bon état. Les bascules sont
  // désactivées pendant l'appel et pendant le chargement, pour ne jamais agir sur des tuiles périmées
  private executer(appel$: Observable<unknown> | undefined) {
    if (!appel$) {
      // La bascule s'est déjà retournée d'elle-même : on la resynchronise sur le store
      this.recharger();
      return;
    }

    this.enregistrementEnCours.set(true);
    appel$
      .pipe(
        take(1),
        finalize(() => {
          this.enregistrementEnCours.set(false);
          this.recharger();
        }),
      )
      .subscribe();
  }

  private recharger() {
    this.rechargement.update(version => version + 1);
  }

  // Le client attendu est celui affiché, pas celui de la tuile : si la tuile est périmée, son index
  // désigne l'accès d'un autre client et le serveur refuse le retrait
  private retirerAccesApplication$(tuile: TuileApplication) {
    const client = this.listeClientsService.tenantSelectionne()?.identifier;
    const indexTenant = tuile.accesExistant?.index;
    return !client || indexTenant === undefined || indexTenant === null
      ? undefined
      : this.listeClientsService.supprimerAccesApplicationConnect$(
          this.famille(),
          tuile.application.index,
          indexTenant,
          client,
        );
  }

  // Le client seulement : le libellé et le client id affichés ne sont pas renvoyés, pour ne pas
  // écraser une modification faite ailleurs
  private accorderAccesFront$(tuile: TuileFront) {
    const client = this.listeClientsService.tenantSelectionne()?.identifier;
    return client
      ? this.listeClientsService.accorderAccesFrontConnect$(this.famille(), tuile.front.index, client)
      : undefined;
  }

  private retirerAccesFront$(tuile: TuileFront) {
    const client = this.listeClientsService.tenantSelectionne()?.identifier;
    const indexTenant = tuile.accesExistant?.index;
    return !client || indexTenant === undefined || indexTenant === null
      ? undefined
      : this.listeClientsService.supprimerAccesFrontConnect$(this.famille(), tuile.front.index, indexTenant, client);
  }

  // Création (sans partenaire) ou modification des informations du partenaire, en modale.
  // Le type est celui de la section : le partenaire est créé sous l'étiquette {type}:{env}
  modifierPartenaire(partenaire?: PartenaireConnect) {
    this.ouvrirInformations('partenaire', partenaire?.partnerName, partenaire?.clientId, resultat =>
      this.listeClientsService.enregistrerPartenaireConnect$(this.famille(), {
        index: partenaire?.index ?? null,
        partnerName: resultat.partnerName,
        clientId: resultat.clientId,
        tenants: [],
      }),
    );
  }

  // Même modale pour une application client (libellé et client id), sous la même étiquette
  modifierApplication(application?: ApplicationConnect) {
    this.ouvrirInformations('application', application?.clientName, application?.clientId, resultat =>
      this.listeClientsService.enregistrerApplicationConnect$(this.famille(), {
        index: application?.index ?? null,
        clientName: resultat.partnerName,
        clientId: resultat.clientId,
      }),
    );
  }

  modifierFront(front?: FrontConnect) {
    this.ouvrirInformations('front', front?.frontName, front?.clientId, resultat =>
      this.listeClientsService.enregistrerFrontConnect$(this.famille(), {
        index: front?.index ?? null,
        frontName: resultat.partnerName,
        clientId: resultat.clientId,
      }),
    );
  }

  private ouvrirInformations(
    nature: NatureServiceConnect,
    nom: string | undefined,
    clientId: string | undefined,
    enregistrer: (resultat: DialogPartenaireConnectOutput) => Observable<unknown>,
  ) {
    const data: DialogPartenaireConnectData = { type: this.famille(), nature, partnerName: nom, clientId };

    this.modal
      .openForm<DialogPartenaireConnectOutput, DialogPartenaireConnectData>(DialogPartenaireConnectComponent, {
        data,
      })
      .closed.subscribe(resultat => {
        if (!resultat) {
          return;
        }

        enregistrer(resultat)
          .pipe(take(1))
          .subscribe(() => this.rechargement.set(this.rechargement() + 1));
      });
  }
}

// LoginId et codes interface sont des int côté backend : au-delà, la requête serait refusée avant
// toute règle métier
const ENTIER_MAXIMAL = 2_147_483_647;

// Règles de Validate du framework : sans Visibility valide l'api ne démarre pas ou retire le
// tenant, Restricted exige un LoginId entier positif, Unrestricted l'interdit
export function accesAVisibiliteValide(acces: TenantApplicationConnect): boolean {
  const visibilite = normaliserVisibilite(acces.visibilite);

  if (visibilite === 'Unrestricted') {
    return acces.loginId === undefined || acces.loginId === null;
  }

  return visibilite === 'Restricted' && entierPositifValide(acces.loginId);
}

// Casse ignorée, comme le backend
function normaliserVisibilite(visibilite: string | undefined | null): VisibiliteConnect | undefined {
  switch (visibilite?.trim().toLowerCase()) {
    case 'restricted':
      return 'Restricted';
    case 'unrestricted':
      return 'Unrestricted';
    default:
      return undefined;
  }
}

function entierPositifValide(texte: string | undefined | null): boolean {
  if (!texte || !/^\d+$/.test(texte.trim())) {
    return false;
  }

  const valeur = Number(texte);
  return valeur > 0 && valeur <= ENTIER_MAXIMAL;
}

function nouveauCode(valeur: string): FormControl<string> {
  return new FormControl<string>(valeur, { nonNullable: true });
}

// Codes à écrire depuis la tuile, ou undefined si la liste est refusée. Les champs laissés vides
// sont ignorés
function codesSaisis(tuile: TuilePartenaire): number[] | undefined {
  const valeurs = tuile.codes
    .getRawValue()
    .map(code => code.trim())
    .filter(code => code !== '');
  return codesValides(valeurs) ? valeurs.map(Number) : undefined;
}

function codesValides(codes: string[]): boolean {
  if (codes.length === 0 || !codes.every(entierPositifValide)) {
    return false;
  }

  const nombres = codes.map(Number);
  return new Set(nombres).size === nombres.length;
}

// Une tuile par partenaire, cochée quand le partenaire a un accès au client (casse et espaces de bord
// ignorés, comme le backend). Les champs reprennent les codes stockés ; un accès nouveau part d'un
// champ vide
export function construireTuilesPartenaires(
  partenaires: PartenaireConnect[],
  identifiantClient: string,
): TuilePartenaire[] {
  const client = identifiantClient.trim().toLowerCase();

  return partenaires.map(partenaire => {
    const accesExistant = partenaire.tenants.find(tenant => tenant.tenantId?.trim().toLowerCase() === client);
    const codesStockes = (accesExistant?.codeInterfaces ?? []).map(code => code.trim());

    return {
      partenaire,
      cochee: !!accesExistant,
      accesExistant,
      codesADefinir: !!accesExistant && !codesValides(codesStockes),
      codes: new FormArray((codesStockes.length > 0 ? codesStockes : ['']).map(nouveauCode)),
    };
  });
}

// Accès à écrire depuis le formulaire de la tuile, ou undefined s'il est incomplet
function accesSaisi(tuile: TuileApplication): AccesApplicationConnect | undefined {
  const { visibilite, loginId } = tuile.formulaire.getRawValue();

  if (visibilite === 'Unrestricted') {
    return { visibilite };
  }

  return entierPositifValide(loginId) ? { visibilite, loginId: Number(loginId) } : undefined;
}

// Une tuile par application, cochée quand l'application a accès au client (casse et espaces de
// bord ignorés, comme le backend). Le formulaire reprend la visibilité
// stockée ; un accès nouveau ou à compléter part sur Restreinte, le choix le plus fermé
export function construireTuilesApplications(
  applications: ApplicationConnect[],
  identifiantClient: string,
): TuileApplication[] {
  const client = identifiantClient.trim().toLowerCase();

  return applications.map(application => {
    const accesExistant = application.tenants.find(tenant => tenant.tenantId?.trim().toLowerCase() === client);
    const visibilite = normaliserVisibilite(accesExistant?.visibilite) ?? 'Restricted';
    const loginId = entierPositifValide(accesExistant?.loginId) ? accesExistant!.loginId!.trim() : '';

    return {
      application,
      cochee: !!accesExistant,
      accesExistant,
      visibiliteADefinir: !!accesExistant && !accesAVisibiliteValide(accesExistant),
      formulaire: new FormGroup({
        visibilite: new FormControl<VisibiliteConnect>(visibilite, { nonNullable: true }),
        loginId: new FormControl<string>(loginId, { nonNullable: true }),
      }),
    };
  });
}

export function construireTuilesFronts(fronts: FrontConnect[], identifiantClient: string): TuileFront[] {
  const client = identifiantClient.trim().toLowerCase();

  return fronts.map(front => {
    const accesExistant = front.tenants.find(tenant => tenant.tenantId?.trim().toLowerCase() === client);

    return { front, cochee: !!accesExistant, accesExistant };
  });
}
